import * as z from "zod";

export const AskRedoPageSkillNameSchema = z.enum([
  "returns",
  "oms",
  "automation",
  "analytics",
  "marketing",
  "shopper-agent",
  "shopper-ai",
  "portal-builder",
  "coverage-builder",
  "marketing-automation-builder",
  "checkoutExperience",
  "checkoutTree",
  "postPurchaseFlow",
  "postPurchaseUpsellPage",
  "spotlights",
  "support-rules",
  "inventory",
]);

export type AskRedoPageSkillName = z.infer<typeof AskRedoPageSkillNameSchema>;

/**
 * Unified page context structure for agents.
 * Used to pass page-specific information from the frontend to agents.
 */
export const PageContextSchema = z.object({
  /**
   * Short, human-readable name of the current page, written for merchants —
   * e.g. "Returns", "Return #1098", "Product analytics". Rendered in the
   * page-context chip above the Ask Redo prompt input so merchants can see
   * (and remove) what page the agent knows they're viewing. Distinct from
   * `pageDescription`, which is a full sentence written for the model.
   */
  pageTitle: z.string().optional(),

  /**
   * Human-readable description of the current page context.
   * Example: "User is viewing return for order number: #1098, return ID: abc123"
   */
  pageDescription: z.string().optional(),

  /**
   * Preferred agent to route to, for legacy triage agents only (v1, v2,
   * external, slack). Values follow the legacy routing enum:
   * "returns-specific", "analytics", "oms", "marketing", "shopper-ai".
   * Ask Redo ignores this field — set `preferredSkill` for Ask Redo routing.
   */
  preferredAgent: z.string().optional(),

  /**
   * Preferred skill for Ask Redo. Must match a registered skill name
   * (for example "returns", "oms", "analytics", "shopper-agent"). Set this on
   * every page that also sets `preferredAgent` so Ask Redo gets a clean,
   * skill-aware routing hint independent of the legacy agent enum.
   */
  preferredSkill: AskRedoPageSkillNameSchema.optional(),

  /**
   * Required skill for Ask Redo page-owned experiences. Unlike
   * `preferredSkill`, this is not just a routing hint: Ask Redo should
   * automatically load this skill when the page context is sent, if the skill
   * is not already active for the thread.
   */
  requiredSkill: AskRedoPageSkillNameSchema.optional(),

  /**
   * Page-specific data as key-value pairs.
   * Structure varies by page type:
   * - Returns page: { returnId: string, orderId: string }
   * - Analytics page: { reportTitle: string, analysisStartDate: string, analysisEndDate: string, reportContent: string }
   */
  pageData: z.record(z.string(), z.unknown()).optional(),

  /**
   * Suggested questions to display in the AI chat interface.
   * These appear as pill buttons that users can click to quickly ask common questions.
   */
  suggestedQuestions: z.array(z.string()).optional(),
});

export type PageContext = z.infer<typeof PageContextSchema>;
